import { useState, useEffect, useRef } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { ThemeProvider, useAppTheme, NAVY, ACCENT, useIsMobile } from "./ThemeContext";
import { AuthProvider, useAuth } from "./AuthContext";
import LoginScreen from "./LoginScreen";
import PullToRefresh, { needsPullToRefresh } from "./PullToRefresh";
import OutboxBanner from "./OutboxBanner";
import SystemStatusBanner from "./SystemStatusBanner";

function getBase(pathname) {
  return pathname.startsWith("/beta") ? "/beta" : "";
}

// Exposed to pages as --shell-bottom / --shell-left so fixed bars can sit clear of the navigation.
const MOBILE_NAV_HEIGHT = 56;
const MOBILE_NAV_OFFSET = `calc(${MOBILE_NAV_HEIGHT}px + env(safe-area-inset-bottom, 0px))`;

function navItems(base) {
  return [
    { path: base || "/", label: "Ülevaade", icon: "📊", roles: ["admin", "site_lead"] },
    { path: `${base}/operator`, label: "Operaator", icon: "🚛" },
    { path: `${base}/time`, label: "Masinatunnid", icon: "⏱" },
    // Prototype: only in the beta for now.
    ...(base === "/beta" ? [
      { path: `${base}/plan`, label: "Objektiplaan", icon: "🗺", roles: ["admin", "site_lead"] },
      { path: `${base}/pm`, label: "Projektijuht", icon: "💶", roles: ["admin"] },
    ] : []),
  ];
}

function ShellContent() {
  const { dark, toggle, t, sidebarOpen, onToggleSidebar } = useAppTheme();
  const { user, loading, logout } = useAuth();
  const mobile = useIsMobile();
  const navigate = useNavigate();
  const contentRef = useRef(null);
  const [pullEnabled] = useState(needsPullToRefresh);
  const location = useLocation();

  useEffect(() => {
    if (mobile && sidebarOpen) onToggleSidebar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const base = getBase(location.pathname);
  const isIndex = location.pathname === (base || "/") || location.pathname === base + "/";
  const canSeeDashboard = !user || user.role === "admin" || user.role === "site_lead";

  useEffect(() => {
    if (!loading && user && isIndex && !canSeeDashboard) navigate(`${base}/operator`, { replace: true });
  }, [loading, user, isIndex, canSeeDashboard, navigate, base]);

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: t.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ color: t.muted, fontSize: 14 }}>Laadin...</div>
      </div>
    );
  }

  if (!user) return <LoginScreen />;

  const isActive = (path) => {
    if (path === (base || "/")) return isIndex;
    return location.pathname.startsWith(path);
  };

  const showUsersNav = user.role === "admin" || user.role === "site_lead";
  const sidebarWidth = 200;

  const allNavItems = [
    ...navItems(base).filter(item => !item.roles || item.roles.includes(user.role)),
    ...(showUsersNav ? [{ path: `${base}/users`, label: "Kasutajad", icon: "👥" }] : []),
    ...(user.role === "admin" ? [{ path: `${base}/audit`, label: "Tegevuslogi", icon: "📜" }] : []),
  ];

  if (mobile) {
    return (
      <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", background: t.bg, "--shell-bottom": MOBILE_NAV_OFFSET }}>
        {!isIndex && (
          <div style={{ background: NAVY, padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ background: ACCENT, width: 28, height: 28, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 14, color: "#fff" }}>S</div>
              <span style={{ color: "#fff", fontWeight: 700, fontSize: 14, letterSpacing: 1 }}>STEINMANN OÜ</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button onClick={toggle} style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", padding: 4 }}>
                {dark ? "☀️" : "🌙"}
              </button>
              <button onClick={logout} style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", padding: 4 }} title="Logi välja">
                🚪
              </button>
            </div>
          </div>
        )}

        {pullEnabled && <PullToRefresh scrollRef={contentRef} color={ACCENT} />}
        <SystemStatusBanner />
        <OutboxBanner />
        <div ref={contentRef} style={{ flex: 1, overflow: "auto", paddingBottom: MOBILE_NAV_OFFSET }}>
          <Outlet />
        </div>

        <div style={{
          display: "flex", background: dark ? "#111118" : "#fff", borderTop: `1px solid ${t.border}`,
          position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 50,
          height: MOBILE_NAV_HEIGHT, paddingBottom: "env(safe-area-inset-bottom, 0px)", boxSizing: "content-box",
        }}>
          {allNavItems.map(item => (
            <button key={item.path} onClick={() => navigate(item.path)} style={{
              flex: 1, padding: "10px 4px", background: "none", border: "none", cursor: "pointer",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
              color: isActive(item.path) ? ACCENT : t.muted,
            }}>
              <span style={{ fontSize: 18 }}>{item.icon}</span>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5 }}>{item.label}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: t.bg, "--shell-left": sidebarOpen ? `${sidebarWidth}px` : "0px" }}>
      {sidebarOpen && (
        <div style={{
          width: sidebarWidth, flexShrink: 0, background: dark ? "#111118" : "#f8f9fb",
          borderRight: `1px solid ${t.border}`, display: "flex", flexDirection: "column",
          padding: "16px 0",
        }}>
          <div style={{ flex: 1 }}>
            {allNavItems.map(item => (
              <button key={item.path} onClick={() => navigate(item.path)} style={{
                width: "100%", padding: "12px 20px", background: isActive(item.path) ? (dark ? "#1a1e27" : "#e8ecf4") : "transparent",
                border: "none", borderLeft: isActive(item.path) ? `3px solid ${ACCENT}` : "3px solid transparent",
                cursor: "pointer", display: "flex", alignItems: "center", gap: 10,
                color: isActive(item.path) ? ACCENT : t.text, fontWeight: isActive(item.path) ? 700 : 500,
                fontSize: 14, textAlign: "left",
              }}>
                <span style={{ fontSize: 16 }}>{item.icon}</span>
                {item.label}
              </button>
            ))}

            <div style={{ borderTop: `1px solid ${t.border}`, margin: "12px 20px" }} />

            <a href="/tools/" target="_blank" rel="noopener noreferrer" style={{
              display: "flex", alignItems: "center", gap: 10, padding: "12px 20px",
              color: t.muted, fontSize: 14, textDecoration: "none",
            }}>
              <span style={{ fontSize: 16 }}>🔧</span>
              Tööriistad ↗
            </a>
          </div>

          {/* User info at bottom */}
          <div style={{ borderTop: `1px solid ${t.border}`, padding: "12px 20px" }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: t.text, marginBottom: 2 }}>{user.name}</div>
            <div style={{ fontSize: 11, color: t.muted, marginBottom: 8 }}>{user.role === "admin" ? "Admin" : user.role === "site_lead" ? "Objektijuht" : "Operaator"}</div>
            <button onClick={logout} style={{
              background: "none", border: "none", color: t.muted, fontSize: 12, cursor: "pointer", padding: 0,
              textDecoration: "underline",
            }}>Logi välja</button>
          </div>
        </div>
      )}

      <div style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>
        {!isIndex && (
          <div style={{
            background: NAVY, padding: "8px 16px", display: "flex", alignItems: "center", gap: 12, flexShrink: 0,
          }}>
            <button onClick={onToggleSidebar} style={{
              background: "none", border: "none", color: "#fff", fontSize: 20, cursor: "pointer", padding: "4px 8px",
            }}>☰</button>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ background: "rgba(255,255,255,0.14)", width: 26, height: 26, borderRadius: 5, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, color: "#fff" }}>S</div>
              <span style={{ color: "#fff", fontWeight: 700, fontSize: 13, letterSpacing: 1 }}>STEINMANN OÜ</span>
            </div>
          </div>
        )}
        <SystemStatusBanner />
        <OutboxBanner />
        <Outlet />
      </div>
    </div>
  );
}

export default function BetaShell() {
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try { return localStorage.getItem("sidebar_open") === "true"; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem("sidebar_open", sidebarOpen ? "true" : "false"); } catch {}
  }, [sidebarOpen]);

  const handleToggle = () => setSidebarOpen(prev => !prev);

  return (
    <ThemeProvider sidebarOpen={sidebarOpen} onToggleSidebar={handleToggle}>
      <AuthProvider>
        <ShellContent />
      </AuthProvider>
    </ThemeProvider>
  );
}
